import React, { useEffect, useMemo, useState } from 'react';
import { cryptoPairLabel } from '../composables/fetchCrypto';
import { useParams, useNavigate } from 'react-router-dom';
import { importSelectedSignals, findSmarterArticlesAll, getArticleById, getGuru, getUserInfo } from '../composables/fetchAPI';
import { signalAssetTypeOf } from '../utils/signalSync';
import dayjs from 'dayjs';
import 'dayjs/locale/th';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { toast } from 'react-hot-toast';
dayjs.extend(localizedFormat);

interface ArticleDetailPageProps {
  allHistoryDataForArticle: any[];
  onRefresh: (isFull?: boolean) => void; // ✅ เพิ่มใน Interface
}

const ArticleDetailPage: React.FC<ArticleDetailPageProps> = ({ allHistoryDataForArticle, onRefresh }) => {
  const { id } = useParams<{ id: string }>();
  const [article, setArticle] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [portfolioName, setPortfolioName] = useState<string>('');
  const [guruName, setGuruName] = useState<string>('');
  const [userInfo, setUserInfo] = useState<any>(null); // เพิ่ม state
  const [importedTickers, setImportedTickers] = useState<string[]>([]);
  const [selectedImage, setSelectedImage] = useState<string | null>(null); // ✅ เพิ่ม State นี้
  const navigate = useNavigate();
  useEffect(() => {
    // ✅ ถ้าเปิดหน้าข่าวมาแล้วข้อมูลยังว่างอยู่ (เช่น กด Refresh หน้าเบราว์เซอร์)
    // ให้สั่ง Fetch ข้อมูลทั้งหมด (isFull = true) ทันที
    if (!allHistoryDataForArticle || allHistoryDataForArticle.length === 0) {
      onRefresh(true);
    }
  }, [allHistoryDataForArticle, onRefresh]);
  // 1. เพิ่ม Logic หาหุ้นที่มีสัญญาณอัปเดต
  const matchingSignals = useMemo(() => {
    // ตรวจสอบความพร้อมของข้อมูล
    if (!article || !article.tickers || !allHistoryDataForArticle || allHistoryDataForArticle.length === 0) {
      return [];
    }

    // ทำ Tickers ให้เป็นตัวพิมพ์ใหญ่และตัดช่องว่าง
    const articleTickers = article.tickers.map((t: string) => t.trim().toUpperCase());

    // กรอง Signals
    return allHistoryDataForArticle.filter(signal =>
      articleTickers.includes(signal.ticker.toUpperCase())
    );
  }, [article, allHistoryDataForArticle]); // React จะคำนวณใหม่เมื่อ Article หรือ Signals เปลี่ยนแปลง

  // useEffect(() => {
  //   if (article && allHistoryDataForArticle) {
  //     console.log("Article Tickers:", article.tickers);
  //     console.log("Signals Sample:", allHistoryDataForArticle);
  //     console.log("Matching Signals:", matchingSignals);
  //   }
  // }, [article, allHistoryDataForArticle]);

  const handleImportDirectly = async (signal: any) => {
    try {
      const ticker = signal.ticker;

      // 1. ยิง API เพื่อบันทึกการ Import ไปยัง Database (Sync กับ Account)
      const result = await importSelectedSignals({
        signalIds: [signal.id],
        tickers: [ticker],
        assetType: signalAssetTypeOf(ticker),
        sync: "" // ส่งว่างไปเพราะเราต้องการแค่ Import ตัวนี้ตัวเดียว
      });

      if (result) {
        // 2. แปลง Format ข้อมูล (เหมือนที่ทำใน HomePage)
        let rawLines = [];
        if (signal.details.lines) {
          rawLines = signal.details.lines;
        } else if (signal.details.S_line || signal.details.R_line) {
          const sLines = Object.entries(signal.details.S_line || {}).map(([tag, price]) => ({
            tag, price: Number(price), type: 'support'
          }));
          const rLines = Object.entries(signal.details.R_line || {}).map(([tag, price]) => ({
            tag, price: Number(price), type: 'resistance'
          }));
          rawLines = [...sLines, ...rLines];
        }

        const dataToSave = {
          lines: rawLines,
          supportTags: rawLines.filter((l: any) => l.type === 'support').map((l: any) => l.tag),
          resistanceTags: rawLines.filter((l: any) => l.type === 'resistance').map((l: any) => l.tag),
        };

        // 3. บันทึกลง LocalStorage ของหุ้นตัวนั้นๆ
        localStorage.setItem(`chartLines_${ticker}`, JSON.stringify(dataToSave));
        setImportedTickers(prev => [...prev, ticker]);
        // 4. แจ้งเตือนความสำเร็จ
        toast.success(`เพิ่มเส้นแนวรับ-แนวต้าน ${cryptoPairLabel(ticker)} สำเร็จ!`, {
          icon: '🚀',
          // style: { background: '#1e3a8a', color: '#fff' }
        });
      }
    } catch (err: any) {
      toast.error(`เกิดข้อผิดพลาด: ${err.message}`);
    }
  };

  const handleUpdateClick = (ticker: string) => {
    // 1. เก็บ Ticker ที่จะเลือก
    localStorage.setItem('selectedSymbol', ticker);

    // 2. สร้าง "คำสั่งพิเศษ" ว่าให้ทำการ Import ทันทีที่ถึงหน้าหลัก
    // เราจะเก็บเป็น Object เพื่อบอกว่าต้องการอัปเดตตัวไหน
    localStorage.setItem('auto_import_signal', JSON.stringify({
      ticker: ticker,
      timestamp: Date.now() // ใส่เวลาไว้เพื่อป้องกันคำสั่งค้างเก่า
    }));

    // navigate('/'); // กลับไปหน้าหลัก
  };


  useEffect(() => {
    const handleTouchStart = (e: TouchEvent) => {
      // ถ้ามีการกดค้างนานๆ ระบบมือถือจะพยายามเลือกข้อความ
      // เราสามารถใส่ logic ป้องกันได้ที่นี่ แต่อาจจะทำให้ scroll ยากขึ้น
      // วิธีที่ดีกว่าคือการคุมผ่าน CSS ด้านบน
    };

    // แต่ถ้าจะกันการก๊อปจริงๆ ให้กัน 'selectstart' ด้วยครับ
    const handleSelectStart = (e: Event) => e.preventDefault();

    document.addEventListener('selectstart', handleSelectStart);
    return () => {
      document.removeEventListener('selectstart', handleSelectStart);
    };
  }, []);

  useEffect(() => {
    getUserInfo().then(setUserInfo).catch(() => setUserInfo(null));

  }, []);

  useEffect(() => {
    window.scrollTo(0, 0); // Scroll to top on mount or id change

    // ปิดคลิกขวา
    const handleContextMenu = (e: MouseEvent) => e.preventDefault();
    document.addEventListener('contextmenu', handleContextMenu);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, [id]);

  useEffect(() => {
    const fetchArticle = async () => {
      setLoading(true);
      try {
        const articleData = await getArticleById(id!); // ใช้ getArticleById เป็นหลัก
        setArticle(articleData); // ตั้งค่าบทความที่ดึงมาได้
      } catch (error) {
        console.error('Error fetching article by ID:', error);
        setArticle(null);
      }
      setLoading(false);
    };
    fetchArticle();
  }, [id]);

  // useEffect(() => {
  //   const fetchArticle = async () => {
  //     setLoading(true);
  //     try {
  //       const res = await findSmarterArticlesAll({ limit: '100' });
  //       const found = res.items?.find((a: any) => a.id === id || a._id === id);
  //       setArticle(found || null);

  //       // Fetch portfolios and find name
  //       if (found?.portfolioId) {
  //         const gurus = await getGuru();
  //         let name = '';
  //         let guru = '';
  //         for (const g of gurus) {
  //           const port = g.portfolios?.find((p: any) => p.id === found.portfolioId);
  //           if (port) {
  //             name = port.name;
  //             guru = g.name;
  //             break;
  //           }
  //         }
  //         setPortfolioName(name || found.portfolioId);
  //         setGuruName(guru);
  //       }
  //     } catch {
  //       setArticle(null);
  //     }
  //     setLoading(false);
  //   };
  //   fetchArticle();
  // }, [id]);

  if (loading) return <div className="p-8">Loading...</div>;
  if (!article) return <div className="p-8 text-red-500">Article not found.</div>;
 const sanitizeContent = (content: string) => {
    if (!content) return '';
    return content
      .replace(/background-color\s*:[^;"]*;?/gi, '') // ลบ background-color
      .replace(/background\s*:[^;"]*;?/gi, '')       // ลบ background เฉยๆ
      .replace(/color\s*:[^;"]*;?/gi, '');           // ลบสีตัวอักษร เพื่อให้มันเปลี่ยนตาม Theme ได้
  };
  
  return (
    <div
      className="p-8 max-w-4xl min-h-screen mx-auto rounded-t-xl shadow-lg"
      style={{
        background: 'var(--component-bg-color)',
        userSelect: 'none',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute', // เปลี่ยนจาก fixed เป็น absolute
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 0,
          opacity: 0.08,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'center',
          userSelect: 'none',
          whiteSpace: 'nowrap',
          gap: '2vw',
        }}
      >
        {Array.from({ length: 5 }).map((_, rowIdx) => (
          <div key={rowIdx} style={{ display: 'flex', gap: '2vw' }}>
            {Array.from({ length: 4 }).map((_, colIdx) => (
              <span
                key={colIdx}
                style={{
                  fontSize: '3vw',
                  fontWeight: 'bold',
                  color: '#888',
                  transform: `rotate(-25deg)`,
                  margin: '2vw',
                  letterSpacing: '2px',
                  whiteSpace: 'nowrap',
                }}
              >
                @ลงทุนหุ้นอเมริกา
              </span>
            ))}
          </div>
        ))}
      </div>
      {/* <button
        className="mb-4 px-4 py-1 bg-gray-200 rounded hover:bg-gray-300"
        onClick={() => navigate(-1)}
      >
        &larr; Back
      </button> */}
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <h1 className="text-2xl font-bold mb-2">{article.title}</h1>
        {Array.isArray(article.tags) && article.tags.length > 0 && (
          <span className=" text-sm font-normal"
            style={{ color: 'var(--secondary-text-color)' }}
          >
            {article.tags.join(' / ')}
          </span>
        )}
      </div>
      {/* ปุ่ม Edit ที่มุมขวาบน (เฉพาะ admin) */}
      {userInfo?.isAdmin && (
        <button
          className="absolute top-6 right-6 bg-yellow-400 hover:bg-yellow-300 text-gray-900 rounded-full p-2 shadow z-10 flex items-center"
          title="แก้ไขบทความ"
          onClick={() => navigate(`/create-article/${article.id || article._id}`)}
          style={{ fontSize: '1.5rem' }}
        >
          {/* ไอคอนดินสอ SVG */}
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536M9 13l6.232-6.232a2 2 0 112.828 2.828L11.828 15.828a2 2 0 01-2.828 0L9 13zm-6 6v-3a2 2 0 012-2h3" />
          </svg>
        </button>
      )}

      {/* <div className="mb-2"
        style={{ color: 'var(--secondary-text-color)' }}>
        <span className="">Portfolio: {portfolioName}</span>
      </div> */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {Array.isArray(article.tickers) && article.tickers.map((ticker: string, idx: number) => (
          <span
            key={idx}
            className="bg-yellow-400 text-gray-900 px-2 py-0.5 rounded-full text-xs font-semibold border border-yellow-300"
          >
            {cryptoPairLabel(ticker)}
          </span>
        ))}
      </div>
      <div className="mb-4 flex items-center gap-2"
        style={{ color: 'var(--secondary-text-color)' }}
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <rect x="3" y="4" width="18" height="18" rx="2" fill="none" stroke="currentColor" strokeWidth="2" />
          <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2" />
          <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2" />
          <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2" />
        </svg>
        <span>
          {dayjs(article.publishedAt).locale('th').format('D MMM YYYY HH:mm [น.]')}
        </span>
        {/* แสดงปุ่มเมื่อเจอ Ticker ที่มีข้อมูลสัญญาณ */}
        <div className="flex gap-2 pl-4 overflow-x-auto no-scrollbar py-2">
          {matchingSignals.map((signal) => {
            const isNew = dayjs().diff(dayjs(signal.details?.lastUpdated), 'hour') < 24;
            const isImported = importedTickers.includes(signal.ticker);
            return (
              <div key={signal.ticker} className="flex flex-col gap-2 min-w-fit">
                {/* --- ปุ่มหลัก: Import ทันที --- */}
                <button
                  onClick={() => handleImportDirectly(signal)}
                  className="group relative flex items-center gap-3 px-6 py-3 rounded-2xl font-black text-white transition-all duration-300 active:scale-95 shadow-[0_10px_20px_rgba(37,99,235,0.2)] border border-white/10"
                  style={{ background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)' }}
                >
                  {/* NEW Tag (ย้ายมาวางตรงนี้) */}
                  {isNew && (
                    <span className="absolute -top-2 -right-1 flex h-4 w-8 items-center justify-center z-20">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full px-1.5 py-0.5 bg-yellow-400 text-[8px] font-black text-blue-900 leading-none">NEW</span>
                    </span>
                  )}

                  {/* <div className="bg-white/10 p-2 rounded-xl">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
            </svg>
          </div> */}

                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    className={`drop-shadow-md ${isNew ? 'text-yellow-300' : 'text-white'}`}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>

                  <div className="flex flex-col items-start leading-tight">
                    <span className="text-[10px] uppercase opacity-70 tracking-widest">รับเส้นแนวรับ-แนวต้าน</span>
                    <span className="text-base">{cryptoPairLabel(signal.ticker)}</span>
                  </div>
                </button>
{/* ✅ ข้อความ/ปุ่ม ไปหน้าเทรด ที่จะโผล่มาเมื่อ Import สำเร็จ */}
        <div className={`transition-all duration-500 flex flex-col items-center ${isImported ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2 pointer-events-none'}`}>
          <button
            onClick={() => {
              localStorage.setItem('selectedSymbol', signal.ticker);
              navigate('/');
            }}
            className="flex items-center gap-2 text-blue-400 font-bold text-xs hover:text-blue-300 group"
          >
            <span>ไปดูกราฟ {cryptoPairLabel(signal.ticker)} ทันที</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="group-hover:translate-x-1 transition-transform">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </button>
          <div className="w-12 h-1 bg-blue-500/20 rounded-full mt-1 animate-pulse" />
        </div>
                {/* --- ปุ่มรอง: ไปที่กระดานเทรด (Go to Chart) --- */}
                {/* <button
          onClick={() => {
            localStorage.setItem('selectedSymbol', signal.ticker);
            navigate('/');
          }}
          className="text-[11px] font-bold text-blue-400 hover:text-blue-300 flex items-center justify-center gap-1 transition-all"
        >
          ไปที่กระดานเทรด <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
        </button> */}
              </div>
            );
          })}
        </div>
      </div>
      {/* Show article image if exists */}
      {article.cover && (
        <div className="mb-6 flex justify-center">
          <img
            src={article.cover}
            alt="Article"
            className="rounded shadow cursor-pointer"
            style={{
              width: '500px',      // Set fixed width
              maxWidth: '90%',    // Responsive for smaller screens
              height: 'auto',      // Keep aspect ratio

            }}
            onClick={() => setSelectedImage(article.cover)} // ✅ เพิ่ม onClick เพื่อเปิดรูปใหญ่
          />
        </div>
      )}
      <div
        className="mt-2 min-h-24 text-sm break-words max-w-full overflow-auto border-t pt-6 prose prose-invert no-copy"
        style={{
          color: 'var(--text-color)',
          borderBlockColor: 'var(--secondary-text-color)',
          WebkitTouchCallout: 'none', // ใส่ inline กันเหนียวสำหรับ iOS
          userSelect: 'none'
        }}
        dangerouslySetInnerHTML={{ __html: sanitizeContent(article.content) }}
      />
      <div className="mt-8 pt-4 border-t  text-sm flex flex-wrap gap-4"
        style={{ color: 'var(--secondary-text-color)', borderBlockColor: 'var(--secondary-text-color)' }}
      >
        <span>{guruName}</span>
{selectedImage && (
        <div
          className="fixed  inset-0 z-[310] flex items-center justify-center bg-black/90 p-4 animate-fadeIn cursor-pointer"
          onClick={() => setSelectedImage(null)} // คลิกที่ไหนก็ได้เพื่อปิด
        >
          <img 
            src={selectedImage} 
            alt="Zoomed Cover" 
            className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl" 
          />
        </div>
      )}


      </div>
    </div>
  );
};

export default ArticleDetailPage;